
import { SLOT_MINUTES, SLOTS_PER_DAY } from '@shared/booking';
import { localRangeToInstants } from '@client/shared/lib/wall-clock-intake.ts';
import { viewerDayKey } from '../week-grid.ts';

export interface InstantRange {
  readonly start: string;
  readonly end: string;
}

export type CellKey = string;

export function cellKey(day: number, slot: number): CellKey {
  return `${day}:${slot}`;
}

export function parseCellKey(key: CellKey): { day: number; slot: number } {
  const [day, slot] = key.split(':');
  return { day: Number(day), slot: Number(slot) };
}

export function rangesToCells(ranges: readonly InstantRange[], days: readonly string[], zone: string): Set<CellKey> {
  const set = new Set<CellKey>();
  days.forEach((day, dayIdx) => {
    for (let slot = 0; slot < SLOTS_PER_DAY; slot++) {
      const { start, end } = localRangeToInstants(day, slot * SLOT_MINUTES, (slot + 1) * SLOT_MINUTES, zone);
      const s = new Date(start).getTime();
      const e = new Date(end).getTime();
      const covered = ranges.some((r) => {
        const rs = new Date(r.start).getTime();
        const re = new Date(r.end).getTime();
        return rs <= s && re >= e;
      });
      if (covered) set.add(cellKey(dayIdx, slot));
    }
  });
  return set;
}

export function cellsToRanges(cells: ReadonlySet<CellKey>, days: readonly string[], zone: string): InstantRange[] {
  const ranges: InstantRange[] = [];
  days.forEach((day, dayIdx) => {
    const slots = [...cells]
      .filter((k) => k.startsWith(`${dayIdx}:`))
      .map((k) => Number(k.split(':')[1]))
      .sort((a, b) => a - b);
    let runStart: number | null = null;
    let prev: number | null = null;
    const flush = (endSlotExclusive: number) => {
      if (runStart === null) return;
      ranges.push(localRangeToInstants(day, runStart * SLOT_MINUTES, endSlotExclusive * SLOT_MINUTES, zone));
      runStart = null;
    };
    for (const slot of slots) {
      if (runStart === null) runStart = slot;
      else if (prev !== null && slot !== prev + 1) {
        flush(prev + 1);
        runStart = slot;
      }
      prev = slot;
    }
    if (prev !== null) flush(prev + 1);
  });
  return ranges;
}

export function localDayKey(isoStart: string, zone: string): string {
  return viewerDayKey(new Date(isoStart), zone);
}

export function mergeVisibleRanges(
  previous: readonly InstantRange[],
  painted: ReadonlySet<CellKey>,
  visibleDays: readonly string[],
  zone: string,
): InstantRange[] {
  const visible = new Set(visibleDays);
  const retained = previous.filter((r) => !visible.has(localDayKey(r.start, zone)));
  return [...retained, ...cellsToRanges(painted, visibleDays, zone)];
}
